/**
 * AllProductsPage Container Component
 *
 * Stateless page container that composes section components.
 * State management is handled by the ProductManagement parent.
 */

// React
import { useCallback, useMemo, useState } from 'react';

// UI primitives
import { Counter } from '@/components/ui/counter';

// Shared components
import { DataTableShortcuts } from '../../DataTable/DataTableShortcuts';
import { FilterChipsList, type FilterChipGroup } from '@components/filters';

// Local components
import { ProductsTabs } from '../Sections/ProductsTabs';
import { ProductsToolbar } from '../Sections/ProductsToolbar';
import { ProductsTable } from '../Sections/ProductsTable';
import { PageTitle } from '../../layouts/Page';
import { FiltersModal } from '../Sections/FiltersModal';
import { StatusFilterBody, STATUS_FILTER_LABEL_KEYS } from '../Sections/StatusFilterBody';
import { FormatFilterBody } from '../Sections/FormatFilterBody';
import { CatalogueFilterBody } from '../Sections/CatalogueFilterBody';
import { DietTagFilterBody } from '../Sections/DietTagFilterBody';
import { AllergenTagFilterBody } from '../Sections/AllergenTagFilterBody';
import { CategoryFilterBody } from '../Sections/CategoryFilterBody';
import { NutritionTagFilterBody } from '../Sections/NutritionTagFilterBody';
import { TemperatureTagFilterBody } from '../Sections/TemperatureTagFilterBody';

// Hooks and utilities
import { useTranslation } from '../../../hooks/useTranslation';
import {
  ALLERGEN_TAG_LABELS,
  DIET_TAG_LABELS,
  NUTRITION_TAG_LABELS,
  SERVING_TEMPERATURE_LABELS,
  humanizeFilterValue,
} from '../Sections/productFilterLabels';
// Types
import type { AllProductsPageProps } from '../types';
import { DEFAULT_FORM_LABELS } from '../types';

const DEFAULT_PRODUCTS: never[] = [];

export function AllProductsPage({
  currentStatus = 'published',
  totalCount = 0,
  onStatusChange,
  isLoading = false,
  isFetching = false,
  products = DEFAULT_PRODUCTS,
  sort,
  direction,
  onSortChange,
  onRowClick,
  onNewProductClick,
  filters,
  filterCount,
  committedFilters,
  dismissHandlers,
  canWrite = false,
  categoryOptions,
}: AllProductsPageProps) {
  const { t } = useTranslation();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const countText = isLoading ? '…' : totalCount.toLocaleString();

  // Seed the modal's pending state from the URL on every open, not just on
  // close. Chips dismiss filters from outside the modal, so without this the
  // modal reopens still holding a value the user has already removed — and the
  // next Apply silently puts it back.
  const handleOpenFilters = useCallback(() => {
    filters.reset();
    setFiltersOpen(true);
  }, [filters]);

  // Build chip groups from committed filter state (URL). Catalogue chips
  // resolve IDs → names via `filters.catalogues` (the fetched list) so users
  // see "MR 2025", not "4". Diet + allergen + nutrition + chef-pick + serving
  // temp collapse into a single "Tag" group to match the Figma.
  const chipGroups = useMemo<FilterChipGroup[]>(() => {
    const cataloguesById = new Map(filters.catalogues.map((c) => [c.id, c]));
    return [
      {
        label: t('chips.format'),
        chips: committedFilters.formats.map((form) => ({
          id: `format:${form}`,
          label: DEFAULT_FORM_LABELS[form],
          onDismiss: () => dismissHandlers.removeFormat(form),
        })),
      },
      {
        label: t('chips.catalogue', 'Catalogue'),
        chips: committedFilters.catalogueIds.flatMap((id) => {
          const catalogue = cataloguesById.get(id);
          if (!catalogue) return [];
          return [{
            id: `catalogue:${id}`,
            label: catalogue.name,
            onDismiss: () => dismissHandlers.removeCatalogue(id),
          }];
        }),
      },
      {
        label: t('chips.status'),
        chips: committedFilters.statuses.map((status) => ({
          id: `status:${status}`,
          label: t(STATUS_FILTER_LABEL_KEYS[status]),
          onDismiss: () => dismissHandlers.removeStatus(status),
        })),
      },
      {
        label: t('chips.category', 'Category'),
        chips: committedFilters.categories.map((cat) => ({
          id: `category:${cat}`,
          label: humanizeFilterValue(cat),
          onDismiss: () => dismissHandlers.removeCategory(cat),
        })),
      },
      {
        label: t('chips.tag', 'Tag'),
        chips: [
          ...committedFilters.dietTags.map((tag) => ({
            id: `diet:${tag}`,
            label: DIET_TAG_LABELS[tag] ?? humanizeFilterValue(tag),
            onDismiss: () => dismissHandlers.removeDietTag(tag),
          })),
          ...committedFilters.allergenTags.map((tag) => ({
            id: `allergen:${tag}`,
            label: ALLERGEN_TAG_LABELS[tag] ?? humanizeFilterValue(tag),
            onDismiss: () => dismissHandlers.removeAllergenTag(tag),
          })),
          ...committedFilters.nutritionTags.map((tag) => ({
            id: `nutrition:${tag}`,
            label: NUTRITION_TAG_LABELS[tag] ?? humanizeFilterValue(tag),
            onDismiss: () => dismissHandlers.removeNutritionTag(tag),
          })),
          ...(committedFilters.chefPick
            ? [{
                id: 'chef:pick',
                label: t('chips.chefs_pick', "chef's pick"),
                onDismiss: () => dismissHandlers.removeChefPick(),
              }]
            : []),
          ...committedFilters.servingTemperatures.map((temp) => ({
            id: `temp:${temp}`,
            label: SERVING_TEMPERATURE_LABELS[temp] ?? humanizeFilterValue(temp),
            onDismiss: () => dismissHandlers.removeServingTemperature(temp),
          })),
        ],
      },
    ];
  }, [t, filters.catalogues, committedFilters, dismissHandlers]);

  return (
    <DataTableShortcuts>
      <div className="flex flex-col gap-4 h-full min-h-0 px-6 sm:px-8 lg:px-16">
        <PageTitle>
          <h1 className="font-kapra-expanded text-2xl font-black uppercase leading-[1.5] text-off-black">
            {t('header.title')}
          </h1>
          <Counter value={countText} data-loading={isLoading || isFetching} />
        </PageTitle>

        <div className="flex flex-shrink-0 items-center justify-between gap-4">
          <ProductsTabs
            currentStatus={currentStatus}
            onStatusChange={onStatusChange}
          />
          <ProductsToolbar
            onFiltersClick={handleOpenFilters}
            onNewProductClick={onNewProductClick}
            filterCount={filterCount}
            canWrite={canWrite}
          />
        </div>

        <FilterChipsList groups={chipGroups} />

        <ProductsTable
          products={products}
          currentFormats={committedFilters.formats}
          currentStatus={currentStatus}
          isLoading={isLoading}
          isFetching={isFetching}
          onRowClick={onRowClick}
          sort={sort}
          direction={direction}
          onSortChange={onSortChange}
          canWrite={canWrite}
          className="flex-1 min-h-0"
        />
      </div>

      <FiltersModal open={filtersOpen} onOpenChange={setFiltersOpen} filters={filters}>
        {filters.activeCategory === 'format' ? (
          <FormatFilterBody
            selectedFormats={filters.selectedFormats}
            onToggle={filters.toggleFormat}
          />
        ) : filters.activeCategory === 'status' ? (
          <StatusFilterBody
            selectedStatuses={filters.selectedStatuses}
            onToggle={filters.toggleStatus}
          />
        ) : filters.activeCategory === 'catalogues' ? (
          <CatalogueFilterBody
            catalogues={filters.catalogues}
            selectedIds={filters.selectedCatalogueIds}
            onToggle={filters.toggleCatalogue}
          />
        ) : filters.activeCategory === 'categories' ? (
          <CategoryFilterBody
            selectedCategories={filters.selectedCategories}
            onToggle={filters.toggleCategory}
            categories={categoryOptions.all}
          />
        ) : filters.activeCategory === 'tags' ? (
          <>
            <DietTagFilterBody
              selectedTags={filters.selectedDietTags}
              onToggle={filters.toggleDietTag}
              chefPickSelected={filters.chefPickSelected}
              onToggleChefPick={filters.toggleChefPick}
            />
            <div className="mt-6">
              <NutritionTagFilterBody
                selectedTags={filters.selectedNutritionTags}
                onToggle={filters.toggleNutritionTag}
              />
            </div>
            <div className="mt-6">
              <AllergenTagFilterBody
                selectedTags={filters.selectedAllergenTags}
                onToggle={filters.toggleAllergenTag}
              />
            </div>
            <div className="mt-6">
              <TemperatureTagFilterBody
                selectedTags={filters.selectedServingTemperatures}
                onToggle={filters.toggleServingTemperature}
              />
            </div>
          </>
        ) : null}
      </FiltersModal>
    </DataTableShortcuts>
  );
}
